import { Dialog } from '@hai/ui-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PresetViewModel } from './preset-models';
import { deletePreset } from './preset-store';
import { generateDataAuto } from '../../util/automation';

interface Props {
  itemsToDelete: PresetViewModel[];
  onSuccessDelete?: (deletedPresets: PresetViewModel[]) => void;
  onCancel?: VoidFunction;
}

export const PresetDeleteModal: React.FunctionComponent<Props> = ({ itemsToDelete, onSuccessDelete, onCancel }) => {
  const { t } = useTranslation();

  const [savedItemsToDelete] = useState(itemsToDelete); // NKE-3757 freeze the list because the parent may reset its selected state if api calls refresh the list

  const handleDelete = (): void => {
    Promise.all(savedItemsToDelete.map((item) => deletePreset(item, t))).then(() => {
      onSuccessDelete?.(savedItemsToDelete);
    });
  };

  const formatPresetNames = (presets: PresetViewModel[]): string => {
    if (!presets.length) {
      return '';
    }

    // Display up to 9 names
    const displayLimit = 9;
    const displayedPresets = presets.slice(0, displayLimit);
    const displayedNames = displayedPresets.map((preset) => {
      const name = preset.name;
      return name.endsWith('.cfg') ? name.slice(0, -4) : name;
    });

    // If there are more than 9, add a "+ X more" suffix
    if (presets.length > displayLimit) {
      const remainingCount = presets.length - displayLimit;
      const lastPart = t('presets.modal.delete.listOfPresetsEnding', { count: remainingCount });
      return `${displayedNames.join(', ')}${lastPart}`;
    }
    return displayedNames.join(', ');
  };

  const listOfPresetNames = formatPresetNames(savedItemsToDelete);
  const body = (
    <>
      <span className="bodyTextBold">{listOfPresetNames}</span>
      <br />
      <br />
      {t('general.cannotUndo')}
    </>
  );
  return (
    <Dialog
      data-auto={generateDataAuto('modal', 'confirm delete')}
      headline={t('presets.modal.delete.title', { count: savedItemsToDelete.length })}
      bodyText={body as unknown as string}
      onClose={onCancel}
      show={true}
      buttons={[
        { label: t('general.cancel') },
        { label: t('general.deleteBtn'), variant: 'danger', onClick: handleDelete },
      ]}
    />
  );
};
